/**
 * Lock layout: the control every page of draggable cards shares. Locking holds
 * the user's arrangement still; a one-time tip teaches the drag.
 *
 * A page adds it in three lines:
 *   const cardsView = usePinnedView('transfer-detail')
 *   <PinMyViewBar view={cardsView} />
 *   <DraggableCardGrid ... locked={cardsView.pinned}>
 */

import { GripVertical, LayoutGrid, Lock, LockOpen, Rows3, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'
import type { PinnedView } from '@/admin/hooks/use-pinned-view'

export function PinMyViewBar({
  view,
  controlsElsewhere = false,
}: {
  view: PinnedView
  /**
   * The page shows `PinMyViewControls` in its own toolbar (e.g. beside the
   * tabs), so this bar keeps only the tip and drops out once it is gone.
   */
  controlsElsewhere?: boolean
}) {
  const showHint = !view.hintSeen && !view.pinned
  if (controlsElsewhere && !showHint) return null
  return (
    <div className="mb-3 flex items-center justify-between gap-3" data-testid="pin-my-view-bar">
      {showHint ? <PinMyViewHint view={view} /> : <span />}
      {controlsElsewhere ? null : <PinMyViewControls view={view} />}
    </div>
  )
}

/** The one-time tip that teaches the drag. */
export function PinMyViewHint({ view }: { view: PinnedView }) {
  const { t } = useTranslation()
  return (
    /* 14px at normal weight on a rose chip: at 11px light the tip went
       unread by older users, and a clipped instruction is no instruction. */
    <span className="flex min-w-0 items-start gap-2.5 rounded-[6px] border-[0.5px] border-[var(--rose-border)] bg-[var(--rose-bg)] px-3.5 py-2.5 text-[14px] font-normal leading-[1.45] text-[var(--rose)]">
      <GripVertical className="mt-[1px] h-4 w-4 shrink-0" />
      <span className="min-w-0">
        {t(
          'detailCards.arrangeHint',
          'This page is yours to arrange. Move cards into the order you work in, then lock the layout to keep it.',
        )}
      </span>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={t('detailCards.dismissHint', 'Dismiss this tip')}
        onClick={view.dismissHint}
        className="-my-1 shrink-0 text-[var(--rose)] hover:bg-[var(--rose-bg)] hover:text-[var(--rose-hover)]"
      >
        <X className="h-4 w-4" />
      </Button>
    </span>
  )
}

/** The layout switch (when the page offers one) and the Lock layout toggle. */
export function PinMyViewControls({
  view,
  className,
}: {
  view: PinnedView
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <div className={cn('flex shrink-0 items-center gap-3', className)}>
      {view.layout && view.setLayout ? (
        <div
          role="group"
          aria-label={t('detailCards.layoutLabel', 'Show cards as')}
          data-testid="cards-layout-switch"
          className="inline-flex rounded-[10px] border border-[var(--rule-strong)] bg-[var(--bg)] p-0.5"
        >
          {(
            [
              ['sections', LayoutGrid, t('detailCards.layoutSections', 'Sections')],
              ['cards', Rows3, t('detailCards.layoutCards', 'Full cards')],
            ] as const
          ).map(([value, Icon, label]) => (
            <Button
              key={value}
              type="button"
              variant="ghost"
              aria-pressed={view.layout === value}
              onClick={() => view.setLayout?.(value)}
              className={cn(
                'h-8 cursor-pointer gap-1.5 rounded-[8px] px-3 text-[14px] font-medium',
                view.layout === value
                  ? 'bg-[var(--bg-subtle)] text-[var(--ink)]'
                  : 'text-[var(--ink-2)] hover:text-[var(--ink)]',
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
            </Button>
          ))}
        </div>
      ) : null}
      {/* Two states that read apart at a glance: locked is a filled accent
          badge with a closed lock; unlocked is a plain outlined button with
          an open lock that invites the click. 13px so it is readable. */}
      <Button
        variant="ghost"
        size="sm"
        onClick={view.togglePin}
        aria-pressed={view.pinned}
        data-testid="pin-my-view"
        data-pinned={view.pinned ? 'true' : 'false'}
        className={
          view.pinned
            ? 'h-8 shrink-0 gap-1.5 rounded-full border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] px-3 text-[13px] font-medium text-[var(--green)] hover:bg-[var(--green-bg)] hover:text-[var(--green)]'
            : 'h-8 shrink-0 gap-1.5 rounded-full border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg-card)] px-3 text-[13px] font-normal text-[var(--ink-2)] hover:border-[var(--green-border)] hover:bg-[var(--bg-card)] hover:text-[var(--green)]'
        }
      >
        {view.pinned ? (
          <>
            <Lock className="h-3.5 w-3.5" />
            {t('detailCards.viewPinned', 'Layout locked')}
          </>
        ) : (
          <>
            <LockOpen className="h-3.5 w-3.5" />
            {t('detailCards.pinView', 'Lock layout')}
          </>
        )}
      </Button>
    </div>
  )
}
